import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  Breadcrumbs,
  Button,
  DeleteModal,
  FormWrapper,
  NewNewsModal,
  SubmitButton,
  TestimonialsTable,
} from "../components";
import { useNews, useProject } from "../hooks";

function TestimonialsNewsPage() {
  const [formData, setFormData] = useState({
    project_name: "",
    project_id: "",
    phase_number: "",
    plot_number: "",
    customer_name: "",
    customer_id: "",
    review: "",
    rating: "",
  });

  const [testimonialData, setTestimonialData] = useState([]);
  const [newsData, setNewsData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteNewsId, setDeleteNewsId] = useState(null);
  const [showNewNewsModal, setShowNewNewsModal] = useState(false);

  const {
    fetchAllTestimonials,
    getCustomersForPlots,
    getProjectNamesWithId,
    addTestimonial,
    editTestimonial,
    deleteTestimonialById,
  } = useProject();
  const { fetchAllNews, deleteNews } = useNews();

  useEffect(() => {
    const fetchInitialData = async () => {
      await Promise.all([getTestimonials(), getNews(), getProjectsList()]);
    };
    fetchInitialData();
  }, []);

  const getTestimonials = async () => {
    const { testimonials, error } = await fetchAllTestimonials();
    if (error) return toast.error(error);
    setTestimonialData(testimonials);
  };

  const getNews = async () => {
    const { news, error } = await fetchAllNews();
    if (error) return toast.error(error);
    setNewsData(news);
  };

  const getProjectsList = async () => {
    const { projects, error } = await getProjectNamesWithId();
    if (error) return toast.error(error);
    setProjects(projects.map((project) => ({ value: project.id, label: project.name })));
  };

  useEffect(() => {
    if (selectedProject && formData.phase_number && formData.plot_number) {
      getCustomersList();
    } else {
      setSelectedCustomer(null);
      setCustomers([]);
    }
  }, [selectedProject, formData.phase_number, formData.plot_number]);

  const getCustomersList = async () => {
    const { phase_number, plot_number } = formData;
    if (!selectedProject || !phase_number || !plot_number) {
      setCustomers([]);
      toast.dismiss();
      toast.error("Please select a project, phase, and plot number first.");
      return;
    }

    const projectId = selectedProject.value;
    setFormData((prev) => ({ ...prev, project_id: projectId }));

    const { customers, error } = await getCustomersForPlots(projectId, phase_number, plot_number);
    if (error) return toast.error("An error occurred while fetching customers");

    const customerList = customers
      .filter((customer) => customer.customer_name)
      .map((customer) => ({
        value: customer.customer_id,
        label: customer.customer_name,
      }));

    if (customerList.length === 0) {
      setCustomers([]);
      setSelectedCustomer(null);
      toast.dismiss();
      toast.error("No customers found for the given plot");
      return;
    }

    setCustomers(customerList);
    if (customerList.length === 1) handleSelectCustomer(customerList[0]);
  };

  const handleSelectCustomer = (selectedOption) => {
    setSelectedCustomer(selectedOption);
    setFormData((prev) => ({ ...prev, customer_id: selectedOption.value }));
  };

  const handleInputChange = (e, useTextCase) => {
    toast.dismiss();
    const { name, value } = e.target;
    const newValue =
      useTextCase === "capitalize"
        ? value.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1))
        : value;

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { customer_id, project_id, review, rating } = formData;

    const { success, error } = await addTestimonial({ customer_id, project_id, review, rating });
    if (error) return toast.error(error);

    toast.success(success);
    await getTestimonials();
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      project_name: "",
      project_id: "",
      phase_number: "",
      plot_number: "",
      customer_name: "",
      customer_id: "",
      review: "",
      rating: "",
    });
    setSelectedCustomer(null);
    setCustomers([]);
    setSelectedProject(null);
  };

  const handleEditTestimonial = async (id, editedData) => {
    const { success, error } = await editTestimonial(id, editedData);
    if (error) return toast.error(error);

    toast.success(success);
    await getTestimonials();
  };

  const handleDeleteTestimonial = async (id) => {
    const { success, error } = await deleteTestimonialById(id);
    if (error) return toast.error(error);

    toast.info(success);
    await getTestimonials();
  };

  const handleDeleteNewsClick = (id) => {
    setShowDeleteModal(true);
    setDeleteNewsId(id);
  };

  const handleDeleteNews = async () => {
    const { success, error } = await deleteNews(deleteNewsId);
    if (error) return toast.error(error);

    toast.info(success);
    setShowDeleteModal(false);
    await getNews();
  };

  const handleAddNews = async () => {
    setShowNewNewsModal(false);
    await getNews();
  };

  const breadcrumbs = [{ name: "Testimonials & News Billboard", link: "/testimonials-news" }];

  return (
    <>
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <section className="section_wrapper">
        <div className="section_title_wrapper">
          <h2 className="section_title">Add Testimonial</h2>
        </div>
        <FormWrapper id="add-testimonial-form" onSubmit={handleSubmit} className="form-w-75">
          <Select
            options={projects}
            value={selectedProject}
            onChange={(newValue) => {
              setSelectedProject(newValue);
              setCustomers([]);
              setSelectedCustomer(null);
              setFormData((prev) => ({ ...prev, project_name: newValue.label, project_id: newValue.value }));
              toast.dismiss();
            }}
            placeholder="Enter project name"
            className="select"
            isSearchable
            required
          />
          <input
            type="number"
            name="phase_number"
            placeholder="Enter phase number"
            value={formData.phase_number}
            onChange={(e) => {
              setCustomers([]);
              handleInputChange(e);
            }}
            className="input"
            required
            min={1}
          />
          <input
            type="number"
            name="plot_number"
            placeholder="Enter plot number"
            value={formData.plot_number}
            onChange={(e) => {
              setCustomers([]);
              handleInputChange(e);
            }}
            className="input"
            required
            min={1}
          />
          <Select
            options={customers}
            value={selectedCustomer}
            onChange={handleSelectCustomer}
            placeholder="Enter customer name"
            className="select"
            isSearchable
            onMenuOpen={getCustomersList}
            required
            styles={{
              control: (base) => ({
                ...base,
                border: "1px solid var(--gray-light)",
                boxShadow: "none",
                ":hover": { border: "1px solid var(--gray-light)" },
              }),
            }}
          />
          <div className="input_wrapper">
            <textarea
              name="review"
              placeholder="Enter testimonial"
              value={formData.review}
              onChange={handleInputChange}
              className="input"
              required
              maxLength={600}
            />
            <span className="end">{formData.review?.length ?? 0}/600</span>
          </div>
          <div className="input_with_suffix_wrapper">
            <input
              type="number"
              name="rating"
              step="0.5"
              min={0.5}
              max={5}
              title="Please enter a number between 0 and 5"
              placeholder="Enter number of stars"
              required
              onChange={handleInputChange}
              value={formData.rating}
              className="input"
            />
            <span className="suffix">/5</span>
          </div>
          <SubmitButton form="add-testimonial-form" start>
            Submit
          </SubmitButton>
        </FormWrapper>
      </section>

      <section className="section_wrapper">
        <div className="section_title_wrapper">
          <h2 className="section_title">Testimonials</h2>
        </div>
        <TestimonialsTable
          data={testimonialData}
          handleEdit={handleEditTestimonial}
          handleDelete={handleDeleteTestimonial}
        />
      </section>

      <section className="section_wrapper">
        <div className="row">
          <h2 className="section_title">News Billboard</h2>
          <Button end className="cta" onClick={() => setShowNewNewsModal(true)}>
            Add news article
          </Button>
        </div>
        {newsData.map((news) => (
          <div key={news.id} className="news_grid">
            <div className="card article_photo">
              <img src={news.photo_url} alt={news.title} className="article_image" />
            </div>
            <div className="article_title row">
              <span>{news.title}</span>
              <Button
                onClick={() => handleDeleteNewsClick(news.id)}
                icon="ic:outline-delete"
                iconPosition="start"
                className="ghost_gray"
              >
                Delete
              </Button>
            </div>
            <pre className="article_content">{news.content}</pre>
          </div>
        ))}
      </section>

      {showNewNewsModal &&
        createPortal(
          <NewNewsModal
            isVisible={showNewNewsModal}
            handleClose={() => setShowNewNewsModal(false)}
            onNewsAdd={handleAddNews}
          />,
          document.body
        )}

      {showDeleteModal &&
        createPortal(
          <DeleteModal
            isVisible={showDeleteModal}
            handleClose={() => setShowDeleteModal(false)}
            handleDelete={handleDeleteNews}
            message="Are you sure you want to delete this news article?"
          />,
          document.body
        )}
    </>
  );
}

export default TestimonialsNewsPage;
